/**
 * Lift2Fit — the support desk (`#/staff`, migration 0038).
 *
 * The staff member's workspace: the chats assigned to me on the left, the queue
 * under them, and the conversation in the middle. It renders WITHOUT the app
 * shell, like the other standalone chat windows, so an agent works in one place.
 *
 * The rules the API enforces are the rules the screen shows: a chat is mine only
 * while I own its session, a queued chat is claimed by taking it, closing ends
 * the session (the history stays) and a signed-off agent gets nothing new.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api, type SupportInbox, type SupportThreadSummary } from '@/lib/api';
import { cn, timeAgo } from '@/lib/format';
import { useAsync } from '../hooks';
import { ChatAvatar } from '../chat';
import { Button } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AlertIcon, ArrowRightIcon, CheckIcon } from '@/lib/icons';
import { EmptyState, LiveMessage, LoadingPanel, Panel, StatusChip } from '../components';

function waitText(iso: string | null): string {
  if (!iso) return '';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - parsed) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m waiting`;
  return `${Math.floor(minutes / 60)}h waiting`;
}

export function StaffScreen() {
  const inboxState = useAsync(() => api.staffInbox(), []);
  const [inbox, setInbox] = useState<SupportInbox | null>(null);
  const [selected, setSelected] = useState<SupportThreadSummary | null>(null);
  const [conversation, setConversation] = useState<{ messages: Array<{ id: string; body: string; authorKind: string; createdAt: string }> } | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (inboxState.data) setInbox(inboxState.data);
  }, [inboxState.data]);

  const refresh = async () => {
    const next = await api.staffInbox();
    if (next.data) setInbox(next.data);
  };

  // Poll, so a queued chat appears while the agent is sitting on the desk.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void refresh();
    }, 10_000);
    return () => window.clearInterval(timer);
  }, []);

  const openSession = async (row: SupportThreadSummary) => {
    setSelected(row);
    setConversation(null);
    setNotice(null);
    if (!row.sessionId) return;
    const result = await api.staffSession(row.sessionId);
    if (result.data) {
      setConversation(result.data);
      return;
    }
    // A queued chat is not mine to read yet: take it, then reload.
    const taken = await api.staffTakeSession(row.sessionId);
    if (taken.problem) {
      setTone('danger');
      setNotice(taken.problem.detail ?? 'That chat could not be taken.');
      return;
    }
    const loaded = await api.staffSession(row.sessionId);
    if (loaded.data) setConversation(loaded.data);
    await refresh();
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [conversation]);

  const sessions = useMemo(
    () => [...(inbox?.assigned ?? []), ...(inbox?.queued ?? [])],
    [inbox],
  );

  const reply = async (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (body === '' || !selected?.sessionId) return;
    setBusy(true);
    const result = await api.staffReplySession(selected.sessionId, body);
    setBusy(false);
    if (!result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That reply did not send.');
      return;
    }
    setDraft('');
    setTone('success');
    setNotice('Sent.');
    const refreshed = await api.staffSession(selected.sessionId);
    if (refreshed.data) setConversation(refreshed.data);
    await refresh();
  };

  const close = async () => {
    if (!selected?.sessionId) return;
    setBusy(true);
    const result = await api.staffCloseSession(selected.sessionId, 'solved');
    setBusy(false);
    if (result.problem) {
      setTone('danger');
      setNotice(result.problem.detail ?? 'That chat could not be closed.');
      return;
    }
    setTone('success');
    setNotice('Closed as solved. The next question starts a new session.');
    setSelected(null);
    setConversation(null);
    await refresh();
  };

  const toggleShift = async () => {
    if (!inbox) return;
    setBusy(true);
    const result = await api.setStaffShift(!inbox.staff.onShift);
    setBusy(false);
    if (result.data) {
      setTone('success');
      setNotice(result.data.onShift ? 'Signed on — queued chats will come to you.' : 'Signed off.');
      await refresh();
    }
  };

  if (inboxState.loading && !inbox) {
    return (
      <div className="l2f-chat-backdrop flex h-screen items-center justify-center p-6">
        <LoadingPanel label="Opening the support desk" rows={3} />
      </div>
    );
  }

  if (!inbox) {
    return (
      <div className="l2f-chat-backdrop flex h-screen items-center justify-center p-6">
        <EmptyState
          title="This account is not on the support team"
          body={
            inboxState.error ??
            'The support desk is for accounts the admin has added as staff. Ask the founder to add you, then come back.'
          }
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
          action={
            <a
              href="#/dashboard"
              className="inline-flex h-10 items-center rounded-pill border border-hairline bg-surface px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
            >
              Back to the app
            </a>
          }
        />
      </div>
    );
  }

  const waiting = inbox.summary.waiting;

  return (
    <div className="l2f-chat-backdrop flex h-screen min-h-0 flex-col" data-testid="staff-desk">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-5 sm:px-6 sm:pt-6">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-chat-lg border border-hairline bg-surface text-accent shadow-float">
            <CheckIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-muted">
              Support desk
            </p>
            <h1 className="truncate font-display text-[22px] font-black uppercase leading-none tracking-[-0.01em] text-ink">
              Your chats
            </h1>
          </div>
          <StatusChip tone={inbox.staff.onShift ? 'success' : 'neutral'}>
            {inbox.staff.onShift ? 'Signed on' : 'Signed off'}
          </StatusChip>
          {waiting > 0 ? (
            <StatusChip tone="warning">{waiting} waiting</StatusChip>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={inbox.staff.onShift ? 'outline' : 'primary'}
            className="rounded-pill"
            disabled={busy}
            data-testid="staff-desk-shift"
            onClick={() => void toggleShift()}
          >
            {inbox.staff.onShift ? 'Sign off' : 'Sign on'}
          </Button>
          <ThemeToggle />
          <a
            href="#/dashboard"
            data-testid="staff-desk-back"
            className="inline-flex h-10 items-center gap-1.5 rounded-pill border border-hairline bg-surface px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink shadow-float transition-colors duration-150 hover:border-accent hover:text-accent"
          >
            Back to the app
            <ArrowRightIcon />
          </a>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 gap-3 p-3 pt-0 sm:gap-4 sm:p-6 sm:pt-0 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="flex min-h-[320px] flex-col gap-3 lg:min-h-0">
          <Panel shape="chat" testId="staff-assigned" title={`Your chats · ${inbox.assigned.length}`} bodyClassName="gap-2 min-h-0 max-h-[38vh] overflow-y-auto p-3 lg:max-h-none">
            {inbox.assigned.length === 0 ? (
              <p className="text-[12.5px] text-ink-muted">
                Nothing assigned. When a chat comes to you it appears here.
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5" data-testid="staff-assigned-list">
                {inbox.assigned.map((row) => (
                  <li key={row.sessionId}>
                    <button
                      type="button"
                      data-testid={`staff-chat-${row.sessionId}`}
                      onClick={() => void openSession(row)}
                      aria-current={selected?.sessionId === row.sessionId}
                      className={cn(
                        'flex w-full items-start gap-2.5 rounded-chat border px-3 py-2 text-left transition-colors duration-150',
                        selected?.sessionId === row.sessionId
                          ? 'border-accent bg-page-subtle shadow-float'
                          : 'border-hairline bg-surface hover:border-accent',
                      )}
                    >
                      <ChatAvatar name={row.displayName} />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-[13px] font-bold text-ink">{row.displayName}</span>
                          {row.unread > 0 ? (
                            <span className="l2f-tabular rounded-pill bg-accent px-1.5 text-[10px] font-bold text-on-accent">
                              {row.unread}
                            </span>
                          ) : null}
                        </span>
                        <span className="truncate text-[11px] text-ink-muted">{row.lastMessage ?? 'No messages yet'}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel shape="chat" testId="staff-queue" title={`Waiting · ${inbox.queued.length}`} bodyClassName="gap-2 min-h-0 max-h-[32vh] overflow-y-auto p-3 lg:max-h-none">
            {inbox.queued.length === 0 ? (
              <p className="text-[12.5px] text-ink-muted">The queue is empty.</p>
            ) : (
              <ul className="flex flex-col gap-1.5" data-testid="staff-queue-list">
                {inbox.queued.map((row) => (
                  <li key={row.sessionId}>
                    <button
                      type="button"
                      data-testid={`staff-queued-${row.sessionId}`}
                      onClick={() => void openSession(row)}
                      className="flex w-full items-start gap-2.5 rounded-chat border border-hairline bg-surface px-3 py-2 text-left hover:border-accent"
                    >
                      <ChatAvatar name={row.displayName} />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[13px] font-bold text-ink">{row.displayName}</span>
                        <span className="truncate text-[11px] text-ink-muted">
                          {row.sessionOpenedAt ? waitText(row.sessionOpenedAt) : 'waiting'} · take to reply
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </aside>

        <main
          className="flex min-h-[520px] min-w-0 flex-col overflow-hidden rounded-chat-lg border border-hairline bg-surface shadow-float lg:min-h-0"
          data-testid="staff-conversation"
        >
          {!selected ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <EmptyState
                className="w-full max-w-[520px] rounded-chat-lg border-dashed bg-surface/60"
                title="Pick a chat"
                body="Your assigned chats and the waiting queue are on the left. Taking a queued chat assigns it to you."
                icon={<CheckIcon className="h-8 w-8 text-accent" />}
              />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-page-subtle px-4 py-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <ChatAvatar name={selected.displayName} size="md" />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-[15px] font-bold text-ink">{selected.displayName}</span>
                    <span className="truncate text-[11.5px] text-ink-muted">
                      {selected.email} · {selected.sessionOpenedAt ? waitText(selected.sessionOpenedAt) : 'waiting'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusChip tone="accent">Yours</StatusChip>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-pill"
                    disabled={busy}
                    data-testid="staff-close"
                    onClick={() => void close()}
                  >
                    Mark solved
                  </Button>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4" data-testid="staff-messages">
                {conversation === null ? (
                  <p className="text-[12.5px] text-ink-muted">Opening…</p>
                ) : conversation.messages.length === 0 ? (
                  <p className="text-[12.5px] text-ink-muted">No messages yet.</p>
                ) : (
                  conversation.messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        'flex flex-col gap-1',
                        message.authorKind === 'user' ? 'items-start' : 'items-end',
                      )}
                    >
                      <span
                        className={cn(
                          'max-w-[72ch] whitespace-pre-wrap px-3.5 py-2.5 text-[13.5px] leading-relaxed text-ink',
                          message.authorKind === 'user'
                            ? 'rounded-bubble rounded-bl-chat-tail bg-page-subtle'
                            : 'rounded-bubble rounded-br-chat-tail bg-bubble-mine',
                        )}
                      >
                        {message.body}
                      </span>
                      <span className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                        {message.authorKind === 'user'
                          ? selected.displayName
                          : message.authorKind === 'bot'
                            ? 'Lift2Fit Assistant'
                            : 'You'}
                      </span>
                    </div>
                  ))
                )}
                <div ref={endRef} />
              </div>

              <form
                onSubmit={reply}
                className="m-3 flex flex-col gap-2 rounded-chat-lg border border-hairline bg-surface p-2 shadow-float"
                data-testid="staff-reply-form"
              >
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                    Reply as Lift2Fit support
                  </span>
                  <textarea
                    data-testid="staff-reply-input"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    rows={2}
                    maxLength={4000}
                    placeholder="Answer in plain language — this arrives in the client’s Help chat."
                    className="l2f-chat-well w-full resize-none rounded-chat px-3.5 py-2.5 text-[14px] text-ink outline-none placeholder:text-ink-muted focus:ring-1 focus:ring-accent"
                  />
                </label>
                <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                  <LiveMessage message={notice} tone={tone} />
                  <Button type="submit" size="sm" className="rounded-pill" disabled={busy || draft.trim() === ''} data-testid="staff-reply-send">
                    {busy ? 'Sending…' : 'Send reply'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
